import { Button, Checkbox, List, ListItem, ListItemIcon, ListItemText, Paper } from "@material-ui/core"
import produce from "immer"
import type { Map as LeafletMap } from "leaflet"
import type { MouseEvent, ReactElement, RefObject } from "react"
import { createRef, useEffect, useMemo, useState } from "react"
import { useMap } from "react-leaflet"
import { connect } from "react-redux"
import type { Dispatch } from "redux"

import { legendSetItem } from "../../../redux"
import type { AppState, LegendItem, MapLegendItem } from "../../../redux"
import { LeafletBottomLeft, disableMapInteraction, enableMapInteraction } from "../../../utils"
import { DragHandle, FreeDraggable, MapControl, MapControlContainer } from "../../atoms"

import "./legend-panel.styles.scss"
import UnfoldLess from "@material-ui/icons/UnfoldLess"
import UnfoldMore from "@material-ui/icons/UnfoldMore"
import { useTranslation } from "react-i18next"

export interface ILegendPanelStateProps {
  legendStateItems?: MapLegendItem[]
  setLegendItems: (mapId: string, items: MapLegendItem[]) => void
}

export interface ILegendPanelProps {
  mapId: string
  prefix?: string
  legendImageUrl?: string
  legendItems?: LegendItem[]
  disableDrag?: boolean | undefined
}

function LegendPanelView(props: ILegendPanelProps & ILegendPanelStateProps): ReactElement {
  const { mapId, prefix, legendItems, legendImageUrl, legendStateItems, setLegendItems, disableDrag } = props
  const { t } = useTranslation("map")

  const popupRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>()
  const [open, setOpen] = useState<boolean>(false)
  const [isOnTopHalf, setIsOnTopHalf] = useState<boolean>(false)
  const dragHandleRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>()
  const map: LeafletMap = useMap()
  const popupMaxHeight: number = (map.getContainer().clientHeight / 100) * 70

  const allLayersVisible: boolean = useMemo<boolean>(() => {
    if (!legendStateItems) return false
    return legendStateItems.every((item: MapLegendItem) => item.active === true)
  }, [legendStateItems])

  const hasLegendToDisplay: boolean = useMemo<boolean>(() => {
    return (legendStateItems !== undefined && legendStateItems.length > 0) || !!legendImageUrl
  }, [legendStateItems, legendImageUrl])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (legendItems) {
      setLegendItems(mapId, legendItems.map(mapLegendItemToLegendItem))
    }
  }, [legendItems])

  function mapLegendItemToLegendItem(legendItem: LegendItem): MapLegendItem {
    const key: string = prefix ? `${prefix}-${legendItem.key}` : legendItem.key
    return {
      title: legendItem.value,
      layerKey: key,
      active: true
    }
  }

  function toggleLegend(event: MouseEvent<HTMLButtonElement>): void {
    if (event.detail !== 1) return
    setOpen(!open)
  }

  function toggleItem(index: number, state: boolean): void {
    if (!legendStateItems) return
    const newItems: MapLegendItem[] = produce(legendStateItems, (draft: MapLegendItem[]) => {
      // biome-ignore lint/style/noParameterAssign: TODO in a later PR
      draft = draft.map((item: MapLegendItem, i: number) => {
        if (i === index || index === -1) {
          item.active = state
        }
        return item
      })
    })
    setLegendItems(mapId, newItems)
  }

  function onMove(position: { x: number; y: number }): void {
    const positiveY: number = Math.abs(position.y - map.getContainer().offsetTop)
    const containerHeight: number = map.getContainer().clientHeight
    setIsOnTopHalf(containerHeight / 2 > positiveY)
  }

  return !hasLegendToDisplay ? (
    <></>
  ) : (
    <MapControlContainer className="legend-panel" position={LeafletBottomLeft}>
      <FreeDraggable onMove={onMove} handleRef={dragHandleRef}>
        <MapControl className="legend-panel-control">
          {!disableDrag && <DragHandle ref={dragHandleRef} />}
          <Button key="legend-panel-control" onClick={toggleLegend} fullWidth>
            {t("legenda")}
            {open ? <UnfoldLess /> : <UnfoldMore />}
          </Button>
        </MapControl>
        {open && (
          <Paper
            onMouseEnter={() => disableMapInteraction(map)}
            onMouseLeave={() => enableMapInteraction(map)}
            square
            ref={popupRef}
            className={`legend-panel-control-popup leaflet-control ${isOnTopHalf ? "display-bottom" : ""}`}
            style={{ maxHeight: popupMaxHeight }}
          >
            {legendImageUrl && <img src={legendImageUrl} alt="legend" />}
            {legendStateItems && (
              <List dense>
                <ListItem dense={true} button onClick={() => toggleItem(-1, !allLayersVisible)}>
                  <ListItemIcon>
                    <Checkbox
                      className="small-checkbox"
                      edge="start"
                      color="primary"
                      checked={allLayersVisible}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("allLayersVisible")} />
                </ListItem>
                {legendStateItems.map((item: MapLegendItem, index: number) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in a later PR
                  <ListItem dense={true} key={index} button onClick={() => toggleItem(index, !item.active as boolean)}>
                    <ListItemIcon>
                      <Checkbox
                        className="small-checkbox"
                        edge="start"
                        color="primary"
                        checked={item.active}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}
      </FreeDraggable>
    </MapControlContainer>
  )
}

function mapStateToProps(state: AppState, ownProps: ILegendPanelProps) {
  return {
    legendStateItems: state.legend.legends[ownProps.mapId]
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    setLegendItems: (mapId: string, items: MapLegendItem[]) => dispatch(legendSetItem(mapId, items))
  }
}

export const LegendPanel = connect(mapStateToProps, mapDispatchToProps)(LegendPanelView)
